import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, BookOpen, Sparkles, ArrowRight, Zap, MessageCircle, PenTool, Shield, AlertTriangle, ExternalLink, Check, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';

export function HomePage() {
    const { t, language } = useLanguage();

    const interactions = [
        { 
            icon: MessageCircle, 
            title: t('aiConversations'), 
            desc: t('aiConversationsDesc'),
            link: '/chat',
            active: true
        },
        { 
            icon: PenTool, 
            title: t('personalJournal'), 
            desc: t('personalJournalDesc'),
            link: '/journal',
            active: true
        },
        { 
            icon: BookOpen, 
            title: t('practicalExercises'), 
            desc: t('practicalExercisesDesc'),
            link: '/exercises',
            active: true
        },
        { 
            icon: Shield, 
            title: t('confidentiality'), 
            desc: t('confidentialityDesc'),
            link: null,
            active: false
        },
    ];

    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="relative py-12 md:py-20 px-4 md:px-8 overflow-hidden" aria-labelledby="hero-title">
                {/* Background glow effects */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-emerald-600/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
                
                <div className="max-w-6xl mx-auto relative">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        {/* Text Content */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-left"
                        >
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-medium mb-6">
                                <Zap className="w-4 h-4" />
                                <span>{t('consciousHuman')}</span>
                            </div>
                            
                            <h1 id="hero-title" className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium text-emerald-50 leading-tight mb-6">
                                {t('heroTitle1')}
                                <span className="text-emerald-400 glow-text"> {t('heroTitle2')}</span>{t('heroTitle3')}
                                <span className="text-emerald-300"> {t('heroTitle4')} </span>{t('heroTitle5')}
                                <span className="text-emerald-400 glow-text"> {t('heroTitle6')}</span>
                            </h1>
                            
                            <p className="text-lg md:text-xl text-emerald-200/80 leading-relaxed mb-8 max-w-xl">
                                {t('heroDescription')}
                            </p>
                        </motion.div>

                        {/* Hero Visual - smaller, positioned higher */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="relative hidden lg:flex items-start justify-center pt-8"
                        >
                            <div className="relative">
                                {/* Glowing orb - reduced size and opacity */}
                                <div className="w-48 h-48 rounded-full bg-gradient-to-br from-emerald-500/15 to-emerald-700/10 blur-xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                                <div className="w-36 h-36 rounded-full bg-gradient-to-br from-emerald-400/20 to-emerald-600/15 blur-lg absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" />
                                <div className="relative w-28 h-28 rounded-full bg-gradient-to-br from-emerald-600/80 to-emerald-800/80 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                                    <Sparkles className="w-12 h-12 text-white/80" />
                                </div>
                            </div>
                            
                            {/* Floating card */}
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.5 }}
                                className="absolute -left-4 bottom-4 bg-[#111916] border border-emerald-800/50 rounded-2xl p-4 shadow-lg max-w-[180px]"
                            >
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                        <Heart className="w-5 h-5 text-emerald-400" />
                                    </div>
                                    <span className="font-heading text-lg font-medium text-emerald-100">{t('presence')}</span>
                                </div>
                                <p className="text-sm text-emerald-400/70">{t('beHereNow')}</p>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA Buttons - Centered below hero */}
            <section className="py-4 px-4 md:px-8">
                <div className="flex flex-wrap justify-center gap-4">
                    <Link to="/chat" data-testid="hero-start-btn">
                        <Button className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-full px-8 py-6 text-lg shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 transition-all hover:-translate-y-0.5 min-w-[280px]">
                            {t('startConversation')}
                            <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                    </Link>
                    <Link to="/exercises" data-testid="hero-exercises-btn">
                        <Button variant="outline" className="border-2 border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 rounded-full px-8 py-6 text-lg min-w-[280px]">
                            {t('exploreExercises')}
                        </Button>
                    </Link>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="py-16 px-4 md:px-8">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <h2 className="font-heading text-3xl md:text-4xl font-medium text-emerald-50 mb-4">
                            {t('pricingTitle')}
                        </h2>
                        <p className="text-lg text-emerald-300/70 max-w-2xl mx-auto">
                            {t('pricingSubtitle')}
                        </p>
                    </motion.div>

                    {/* Pricing Cards */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* START */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0 }}
                            className="relative bg-gradient-to-b from-[#111916] to-[#0A0F0D] rounded-2xl border border-emerald-800/40 p-6 hover:border-emerald-600/60 transition-all hover:shadow-lg hover:shadow-emerald-900/20"
                        >
                            <h3 className="text-emerald-400 font-bold text-lg mb-2">{t('planStart')}</h3>
                            <div className="flex items-baseline gap-1 mb-4">
                                <span className="text-4xl font-bold text-emerald-100">5</span>
                                <span className="text-xl text-emerald-400">€</span>
                            </div>
                            <ul className="space-y-2 mb-6 text-sm">
                                <li className="flex items-center gap-2 text-emerald-300">
                                    <Check className="w-4 h-4 text-emerald-500" />
                                    20 {t('responses')}
                                </li>
                                <li className="flex items-center gap-2 text-emerald-400/70">
                                    <Clock className="w-4 h-4" />
                                    {t('validity')}: 1 {t('month')}
                                </li>
                                <li className="flex items-center gap-2 text-emerald-500/60 text-xs">
                                    {t('costPerResponse')}: 0.25€
                                </li>
                            </ul>
                            <a href="https://buy.stripe.com/3cI4gA8Eb4nu9nb2nd0Fi07" target="_blank" rel="noopener noreferrer">
                                <Button data-testid="pricing-btn-start" className="w-full bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-300 border border-emerald-600/40 rounded-xl">
                                    {t('getAccess')}
                                </Button>
                            </a>
                        </motion.div>

                        {/* STANDARD - Popular */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="relative bg-gradient-to-b from-emerald-900/40 to-[#0A0F0D] rounded-2xl border-2 border-emerald-500/60 p-6 hover:border-emerald-400/80 transition-all hover:shadow-lg hover:shadow-emerald-500/20 scale-105"
                        >
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                                {t('popular')}
                            </div>
                            <h3 className="text-emerald-300 font-bold text-lg mb-2">{t('planStandard')}</h3>
                            <div className="flex items-baseline gap-1 mb-4">
                                <span className="text-4xl font-bold text-emerald-100">10</span>
                                <span className="text-xl text-emerald-400">€</span>
                            </div>
                            <ul className="space-y-2 mb-6 text-sm">
                                <li className="flex items-center gap-2 text-emerald-300">
                                    <Check className="w-4 h-4 text-emerald-500" />
                                    50 {t('responses')}
                                </li>
                                <li className="flex items-center gap-2 text-emerald-400/70">
                                    <Clock className="w-4 h-4" />
                                    {t('validity')}: 3 {t('months')}
                                </li>
                                <li className="flex items-center gap-2 text-emerald-500/60 text-xs">
                                    {t('costPerResponse')}: 0.20€
                                </li>
                            </ul>
                            <a href="https://buy.stripe.com/6oU8wQ3jR4nufLz9PF0Fi08" target="_blank" rel="noopener noreferrer">
                                <Button data-testid="pricing-btn-standard" className="w-full bg-emerald-500 hover:bg-emerald-400 text-white rounded-xl shadow-lg shadow-emerald-500/30">
                                    {t('getAccess')}
                                </Button>
                            </a>
                        </motion.div>

                        {/* PLUS */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                            className="relative bg-gradient-to-b from-[#111916] to-[#0A0F0D] rounded-2xl border border-emerald-800/40 p-6 hover:border-emerald-600/60 transition-all hover:shadow-lg hover:shadow-emerald-900/20"
                        >
                            <h3 className="text-emerald-400 font-bold text-lg mb-2">{t('planPlus')}</h3>
                            <div className="flex items-baseline gap-1 mb-4">
                                <span className="text-4xl font-bold text-emerald-100">50</span>
                                <span className="text-xl text-emerald-400">€</span>
                            </div>
                            <ul className="space-y-2 mb-6 text-sm">
                                <li className="flex items-center gap-2 text-emerald-300">
                                    <Check className="w-4 h-4 text-emerald-500" />
                                    350 {t('responses')}
                                </li>
                                <li className="flex items-center gap-2 text-emerald-400/70">
                                    <Clock className="w-4 h-4" />
                                    {t('validity')}: 6 {t('months')}
                                </li>
                                <li className="flex items-center gap-2 text-emerald-500/60 text-xs">
                                    {t('costPerResponse')}: 0.14€
                                </li>
                            </ul>
                            <a href="https://buy.stripe.com/aFa7sM9If3jq42R1j90Fi09" target="_blank" rel="noopener noreferrer">
                                <Button data-testid="pricing-btn-plus" className="w-full bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-300 border border-emerald-600/40 rounded-xl">
                                    {t('getAccess')}
                                </Button>
                            </a>
                        </motion.div>

                        {/* PRO */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3 }}
                            className="relative bg-gradient-to-b from-[#111916] to-[#0A0F0D] rounded-2xl border border-emerald-800/40 p-6 hover:border-emerald-600/60 transition-all hover:shadow-lg hover:shadow-emerald-900/20"
                        >
                            <h3 className="text-emerald-400 font-bold text-lg mb-2">{t('planPro')}</h3>
                            <div className="flex items-baseline gap-1 mb-4">
                                <span className="text-4xl font-bold text-emerald-100">100</span>
                                <span className="text-xl text-emerald-400">€</span>
                            </div>
                            <ul className="space-y-2 mb-6 text-sm">
                                <li className="flex items-center gap-2 text-emerald-300">
                                    <Check className="w-4 h-4 text-emerald-500" />
                                    1000 {t('responses')}
                                </li>
                                <li className="flex items-center gap-2 text-emerald-400/70">
                                    <Clock className="w-4 h-4" />
                                    {t('validity')}: 12 {t('months')}
                                </li>
                                <li className="flex items-center gap-2 text-emerald-500/60 text-xs">
                                    {t('costPerResponse')}: 0.10€
                                </li>
                            </ul>
                            <a href="https://buy.stripe.com/fZufZi4nVdY40QFbXN0Fi0a" target="_blank" rel="noopener noreferrer">
                                <Button data-testid="pricing-btn-pro" className="w-full bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-300 border border-emerald-600/40 rounded-xl">
                                    {t('getAccess')}
                                </Button>
                            </a>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Critical Disclaimer Banner - Before Start Conversation */}
            <motion.section 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="py-8 px-4 md:px-8"
                role="alert"
                aria-label={t('disclaimerTitle')}
            >
                <div className="max-w-4xl mx-auto">
                    <div className="bg-gradient-to-r from-emerald-950/80 to-teal-900/60 border border-teal-500/40 rounded-xl p-4 md:p-5 shadow-lg text-center">
                        <h3 className="text-teal-300 font-bold text-sm md:text-base mb-3 flex items-center justify-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-400" aria-hidden="true" />
                            {t('disclaimerTitle')} 
                            <AlertTriangle className="w-4 h-4 text-amber-400" aria-hidden="true" />
                        </h3>
                        <p className="text-teal-200/90 text-xs md:text-sm mb-2">
                            {t('disclaimerText1')}
                        </p>
                        <p className="text-teal-200/90 text-xs md:text-sm mb-4">
                            {t('disclaimerText2')}
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
                            <span className="text-teal-300 font-bold text-sm">
                                {t('disclaimerCrisis')}
                            </span>
                            <a 
                                href="https://findahelpline.com/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                data-testid="helpline-link"
                                aria-label={t('findHelpline')}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/30 hover:bg-teal-500/40 border border-teal-400/50 rounded-lg text-teal-200 hover:text-white text-xs md:text-sm font-medium transition-all hover:-translate-y-0.5"
                            >
                                <ExternalLink className="w-4 h-4" aria-hidden="true" />
                                {t('findHelpline')}
                            </a>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* Categories Section */}
            <section className="py-16 md:py-24 px-4 md:px-8">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <h2 className="font-heading text-3xl md:text-4xl font-medium text-emerald-50 mb-4">
                            {language === 'ro' ? 'Începe o Conversație' : 'Start a Conversation'}
                        </h2>
                        <p className="text-lg text-emerald-300/70 max-w-2xl mx-auto mb-8">
                            {language === 'ro' 
                                ? 'Scrie orice te preocupă - anxietate, traume, emoții, relații sau dezvoltare personală. AI-ul înțelege automat contextul și îți oferă ghidare personalizată.'
                                : 'Write about anything that concerns you - anxiety, trauma, emotions, relationships or personal growth. AI automatically understands context and provides personalized guidance.'}
                        </p>
                        
                        {/* Single unified chat button */}
                        <Link
                            to="/chat"
                            data-testid="start-chat-btn"
                            className="inline-flex items-center gap-3 px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/30 hover:-translate-y-1"
                        >
                            <MessageCircle className="w-6 h-6" />
                            {language === 'ro' ? 'Începe Conversația' : 'Start Conversation'}
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                        
                        {/* Topics hint */}
                        <div className="flex flex-wrap justify-center gap-2 mt-8 text-sm text-emerald-500/60">
                            <span className="px-3 py-1 bg-emerald-900/30 rounded-full">{language === 'ro' ? 'anxietate' : 'anxiety'}</span>
                            <span className="px-3 py-1 bg-emerald-900/30 rounded-full">{language === 'ro' ? 'traume' : 'trauma'}</span>
                            <span className="px-3 py-1 bg-emerald-900/30 rounded-full">{language === 'ro' ? 'emoții' : 'emotions'}</span>
                            <span className="px-3 py-1 bg-emerald-900/30 rounded-full">{language === 'ro' ? 'relații' : 'relationships'}</span>
                            <span className="px-3 py-1 bg-emerald-900/30 rounded-full">{language === 'ro' ? 'dezvoltare' : 'growth'}</span>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* INTERACTIONS Section - Active Features */}
            <section className="py-16 md:py-24 px-4 md:px-8">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <h2 className="font-heading text-2xl md:text-3xl font-medium text-emerald-400 mb-2 tracking-wider">
                            {t('interactions')}
                        </h2>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {interactions.map((feature, index) => {
                            const Icon = feature.icon;
                            const content = (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                    className={`bg-[#111916]/60 backdrop-blur-sm border border-emerald-800/30 rounded-2xl p-6 text-center h-full ${feature.active ? 'hover:border-emerald-600/50 hover:shadow-lg hover:shadow-emerald-500/10 transition-all cursor-pointer group' : ''}`}
                                >
                                    <div className={`w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-4 ${feature.active ? 'group-hover:bg-emerald-500/30 transition-all' : ''}`}>
                                        <Icon className="w-7 h-7 text-emerald-400" />
                                    </div>
                                    <h3 className="font-heading text-lg font-medium text-emerald-100 mb-2">
                                        {feature.title}
                                    </h3>
                                    <p className="text-emerald-300/80 text-sm">
                                        {feature.desc}
                                    </p>
                                    {feature.active && (
                                        <div className="mt-4 flex items-center justify-center text-emerald-400 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                                            <ArrowRight className="w-3 h-3 mr-1" />
                                            {t('startNow')}
                                        </div>
                                    )}
                                </motion.div>
                            );

                            if (feature.active && feature.link) {
                                return (
                                    <Link key={index} to={feature.link} className="block h-full">
                                        {content}
                                    </Link>
                                );
                            }
                            return <div key={index} className="h-full">{content}</div>;
                        })}
                    </div>
                </div>
            </section>

            {/* Examples Banner */}
            <section className="py-12 md:py-16 px-4 md:px-8">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="bg-gradient-to-br from-purple-900/30 via-emerald-900/30 to-teal-900/30 border border-emerald-600/40 rounded-3xl p-8 md:p-10 relative overflow-hidden"
                    >
                        {/* Decorative elements */}
                        <div className="absolute top-0 left-0 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 right-0 w-40 h-40 bg-emerald-500/20 rounded-full blur-3xl" />
                        
                        <div className="text-center mb-8 relative">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-300 text-sm mb-4">
                                <Sparkles className="w-4 h-4" />
                                {language === 'ro' ? 'Acces Gratuit' : 'Free Access'}
                            </div>
                            <h2 className="font-heading text-2xl md:text-3xl font-medium text-emerald-50">
                                ✨ {language === 'ro' ? 'Experimentează Puterea Iulian AI' : 'Experience the Power of Iulian AI'} ✨
                            </h2>
                            <p className="text-emerald-300/70 mt-2 max-w-2xl mx-auto">
                                {language === 'ro' 
                                    ? 'Vezi cum Iulian AI răspunde la întrebări reale despre emoții, traume și educație'
                                    : 'See how Iulian AI responds to real questions about emotions, trauma, and education'}
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-3 gap-4 md:gap-6 relative">
                            {/* Emotion Example */}
                            <Link 
                                to="/example/emotie" 
                                data-testid="example-emotie"
                                className="group"
                            >
                                <motion.div 
                                    whileHover={{ scale: 1.02 }}
                                    className="bg-[#111916]/80 backdrop-blur-sm border border-emerald-700/40 rounded-2xl p-5 text-center hover:border-emerald-500/50 transition-all cursor-pointer h-full"
                                >
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/30 to-orange-500/30 border border-amber-500/30 flex items-center justify-center mx-auto mb-3">
                                        <Sparkles className="w-6 h-6 text-amber-400" />
                                    </div>
                                    <h3 className="font-heading text-lg font-medium text-emerald-100 mb-1">
                                        {language === 'ro' ? 'Emoții' : 'Emotions'}
                                    </h3>
                                    <p className="text-emerald-400/70 text-sm mb-3">
                                        {language === 'ro' ? 'Alchimia Trădării' : 'The Alchemy of Betrayal'}
                                    </p>
                                    <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-medium group-hover:text-emerald-300 transition-colors">
                                        {language === 'ro' ? 'Vezi exemplu' : 'See example'}
                                        <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                                    </span>
                                </motion.div>
                            </Link>
                            
                            {/* Trauma Example */}
                            <Link 
                                to="/example/trauma" 
                                data-testid="example-trauma"
                                className="group"
                            >
                                <motion.div 
                                    whileHover={{ scale: 1.02 }}
                                    className="bg-[#111916]/80 backdrop-blur-sm border border-emerald-700/40 rounded-2xl p-5 text-center hover:border-emerald-500/50 transition-all cursor-pointer h-full"
                                >
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500/30 to-red-500/30 border border-rose-500/30 flex items-center justify-center mx-auto mb-3">
                                        <Heart className="w-6 h-6 text-rose-400" />
                                    </div>
                                    <h3 className="font-heading text-lg font-medium text-emerald-100 mb-1">
                                        {language === 'ro' ? 'Traume' : 'Trauma'}
                                    </h3>
                                    <p className="text-emerald-400/70 text-sm mb-3">
                                        {language === 'ro' ? 'Dilema Chirurgului' : "The Surgeon's Dilemma"}
                                    </p>
                                    <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-medium group-hover:text-emerald-300 transition-colors">
                                        {language === 'ro' ? 'Vezi exemplu' : 'See example'}
                                        <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                                    </span>
                                </motion.div>
                            </Link>
                            
                            {/* Education Example */}
                            <Link 
                                to="/example/educatie" 
                                data-testid="example-educatie"
                                className="group"
                            >
                                <motion.div 
                                    whileHover={{ scale: 1.02 }}
                                    className="bg-[#111916]/80 backdrop-blur-sm border border-emerald-700/40 rounded-2xl p-5 text-center hover:border-emerald-500/50 transition-all cursor-pointer h-full"
                                >
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/30 to-cyan-500/30 border border-blue-500/30 flex items-center justify-center mx-auto mb-3">
                                        <BookOpen className="w-6 h-6 text-blue-400" />
                                    </div>
                                    <h3 className="font-heading text-lg font-medium text-emerald-100 mb-1">
                                        {language === 'ro' ? 'Educație' : 'Education'}
                                    </h3>
                                    <p className="text-emerald-400/70 text-sm mb-3">
                                        {language === 'ro' ? 'Educație Sustenabilă' : 'Sustainable Education'}
                                    </p>
                                    <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-medium group-hover:text-emerald-300 transition-colors">
                                        {language === 'ro' ? 'Vezi exemplu' : 'See example'}
                                        <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                                    </span>
                                </motion.div>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 md:py-24 px-4 md:px-8">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="bg-gradient-to-br from-emerald-900/50 to-emerald-950/50 border border-emerald-700/30 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden"
                    >
                        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                        
                        <h2 className="font-heading text-3xl md:text-4xl font-medium text-emerald-50 mb-4 relative">
                            {t('readyToStart')}
                        </h2>
                        <p className="text-emerald-300/80 text-lg mb-8 max-w-xl mx-auto relative">
                            {t('everyStepCounts')}
                        </p>
                        <Link to="/chat" data-testid="cta-start-btn">
                            <Button className="bg-emerald-500 text-white hover:bg-emerald-400 rounded-full px-8 py-6 text-lg font-medium shadow-lg shadow-emerald-500/30 relative">
                                {t('startNow')}
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 px-4 md:px-8 border-t border-emerald-900/30">
                <div className="max-w-6xl mx-auto text-center space-y-4">
                    <p className="text-emerald-400/80 text-sm">
                        {t('footerText')}
                    </p>
                    <p className="text-emerald-500/70 text-xs italic">
                        {t('footerAiDisclaimer')}
                    </p>
                    <p className="text-emerald-400/80 text-xs">
                        {t('footerWarning')}
                    </p>
                    
                    {/* Company Info - English Only */}
                    <div className="pt-4 mt-4 border-t border-emerald-800/30 text-emerald-400/70 text-xs space-y-1">
                        <p className="font-medium text-emerald-300">TRIBOIAI LLC</p>
                        <p>File No. 2026-001869654</p>
                        <p>1021 E Lincolnway Suite 9733, Cheyenne, WY 82001, USA</p>
                        <p className="pt-2">
                            <a 
                                href="mailto:contact@iulianai.online"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-emerald-400 hover:text-emerald-300 underline transition-colors"
                            >
                                contact@iulianai.online
                            </a>
                        </p>
                        <p className="pt-2 text-emerald-500/80">© 2026. All Rights Reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default HomePage;
